import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import FormControl from '@mui/material/FormControl'
import Checkbox from '@mui/material/Checkbox'
import FormLabel from '@mui/material/FormLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Add from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import ImageList from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'
import ImageListItemBar from '@mui/material/ImageListItemBar'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import AddressSearchModal from '../../component/AddressSearchModal'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applySportDraft,
    clearSportDraft,
    createEditSportDraftKey,
    createSportDraftPayload,
    readSportDraft,
    saveSportDraft,
} from './sportDraftUtils'
import {
    SPORT_IMAGE_LEAVE_MESSAGE,
    hasPendingSportImages,
    usePendingSportImageUnloadGuard,
} from './sportLeaveGuard'

const MAX_COUNT = 30
const IMAGE_FILE_TYPES = ['image/png', 'image/jpg', 'image/jpeg']

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    productId: null,
    sportsAdminId: 1,
    type: 1,
    category: 1,
    title: '',
    phone: '',
    address: '',
    addressDetail: '',
    latitude: '',
    longitude: '',
    openingHours: '',
    info: '',
    price: '',
    options: '',
    price1: '',
    price1Sale: '',
    price3: '',
    price3Sale: '',
    price6: '',
    price6Sale: '',
    price12: '',
    price12Sale: '',
    neeGym: 0,
    daGym: 0,
    memo: '',
})

export default function Sport(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.sportsId
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || {
        searchType: 99,
        searchPrice: 99,
        searchCategory: '전체',
        searchCategory2: '',
    }
    const curPage = locationState.curPage || 1
    const listSearch = location.search || ''
    const draftKey = createEditSportDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openModal, setOpenModal] = useState(false)
    const [checked, setChecked] = useState([])
    const [mainImage, setMainImage] = useState('')
    const [uploadFiles, setUploadFiles] = useState([])
    const [newUploadFiles, setNewUploadFiles] = useState([])
    const [newUploadFiles2, setNewUploadFiles2] = useState([])
    const [fileLimit, setFileLimit] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = hasPendingSportImages(params, newUploadFiles) && !saving && !navigationUnlocked

    usePendingSportImageUnloadGuard(leaveGuardEnabled)

    const resetDetailState = useCallback((errorMessage) => {
        setParams(createDefaultParams())
        setChecked([])
        setMainImage('')
        setUploadFiles([])
        setNewUploadFiles([])
        setNewUploadFiles2([])
        setFileLimit(false)
        setOpenModal(false)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 운동시설 상세를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/sports/getDetail', { sportsId: id })

                if (data.code === 200) {
                    const baseData = data.data || {}

                    if (!baseData.sportsId) {
                        resetDetailState('운동시설 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const images = (baseData.images || '').split(',').filter(Boolean)
                    const baseChecked = (baseData.options || '').split(',').filter(Boolean)
                    const nextParams = { ...baseData }
                    const priceArr = (baseData.price || '').split(',').filter(Boolean)

                    for (let i = 0; i < priceArr.length; i++) {
                        let pArr = priceArr[i].split(':')
                        nextParams[`price${pArr[0]}`] = pArr[1]
                        nextParams[`price${pArr[0]}Sale`] = pArr[2]
                    }

                    const storedDraft = readSportDraft(draftKey)
                    const restoredDraft = applySportDraft({
                        baseParams: nextParams,
                        baseChecked,
                        baseUploadFiles: images,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createSportDraftPayload({
                        params: nextParams,
                        checked: baseChecked,
                        uploadFiles: images,
                    }))

                    setHasDetail(Boolean(baseData?.sportsId))
                    setMainImage(baseData.mainImage === null ? '' : `${process.env.REACT_APP_AWS_S3_URL}/${baseData.mainImage}`)
                    if (images.length >= MAX_COUNT) {
                        setFileLimit(true)
                    }
                    setUploadFiles(restoredDraft.uploadFiles)
                    setChecked(restoredDraft.checked)
                    setParams(restoredDraft.params)
                    setOpenModal(restoredDraft.openModal)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '운동시설 정보를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '운동시설 정보를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        if ((uploadFiles.length + newUploadFiles.length) < MAX_COUNT) {
            setFileLimit(false)
        } else {
            setFileLimit(true)
        }
    }, [uploadFiles, newUploadFiles, newUploadFiles2])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 운동시설 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createSportDraftPayload({
            params,
            checked,
            uploadFiles,
            openModal,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearSportDraft(draftKey)
            return
        }

        saveSportDraft(draftKey, nextDraft)
    }, [checked, draftKey, draftReady, hasDetail, openModal, params, uploadFiles])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        // 저장 후에도 운영자가 보던 운동시설 목록 조건으로 그대로 복귀시킨다.
        clearSportDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/sports',
                search: listSearch,
                searchText: searchText,
                searchSelect: searchSelect,
                curPage: curPage,
                ...snackbarState,
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (leaveGuardEnabled) {
            event?.preventDefault()

            if (!window.confirm(SPORT_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const file = e.target.files[0]

        if (!file) {
            return
        }

        if (!IMAGE_FILE_TYPES.includes(file.type)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('png, jpg, jpeg 형식의 이미지만 업로드할 수 있습니다.')
            e.target.value = ''
            return
        }

        let reader = new FileReader()

        reader.onloadend = () => {
            setParams((prev) => ({
                ...prev,
                [e.target.name]: file
            }))
            setMainImage(reader.result)
        }
    
        reader.readAsDataURL(file)
    }

    const handleUploadClickMulti =  (e) => {
        const files = Array.prototype.slice.call(e.target.files)
        const uploaded = [...newUploadFiles]
        let fileURLs = []

        files.some((file, index) => {
            if (!IMAGE_FILE_TYPES.includes(file.type)) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('png, jpg, jpeg 형식의 이미지만 업로드할 수 있습니다.')
                return true
            }

            if (uploaded.findIndex((f) => f.name === file.name) === -1) {        
                if ( ( uploaded.length + uploadFiles.length ) >= MAX_COUNT) {
                    alert(`최대 ${MAX_COUNT}장의 사진만 업로드 가능합니다.`)
                    setFileLimit(true)
                    return true
                }

                uploaded.push(file)
                setFileLimit(false)
                let reader = new FileReader()
                reader.onloadend = () => {
                    fileURLs[index] = reader.result
                    setNewUploadFiles2([...newUploadFiles2, ...fileURLs])
                }

                reader.readAsDataURL(file)
            }

            return false
        })
        
        setNewUploadFiles(uploaded)
        e.target.value = ''
    }

    const handleImageDelete = (index) => {
        setUploadFiles(uploadFiles.filter((item,i) => i !== index))
    }

    const handleNewImageDelete = (index) => {
        setNewUploadFiles(newUploadFiles.filter((item,i) => i !== index))
        setNewUploadFiles2(newUploadFiles2.filter((item,i) => i !== index))
    }

    const updateData = async (params) => {
        setSaving(true)
        try {
            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/sports/update',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                moveToList({ openSnackbar: true, severitySnackbar: 'success', messageSnackbar: data.message })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('운동시설을 수정하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleComplete = (res) => {
        let fullAddress = res.address
        let extraAddress = ""

        if (res.addressType === "R") {
            if (res.bname !== "") {
                extraAddress += res.bname
            }
            if (res.buildingName !== "") {
                extraAddress += extraAddress !== "" ? `, ${res.buildingName}` : res.buildingName
            }
            fullAddress += extraAddress !== "" ? ` (${extraAddress})` : ""
        }

        setOpenModal(false)
        setParams((prev) => ({
            ...prev,
            country: res.sido.length > 2 ? res.sido : res.sido,
            area: res.sigungu,
            address: fullAddress,
        }))

        async function getLatLon(fullAddress) {
            const options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8", 
                    "Authorization": `KakaoAK ${process.env.REACT_APP_KAKAO_API_KEY}`
                }
            }
            let response = await fetch(`https://dapi.kakao.com/v2/local/search/address.json?query=${fullAddress}`, options)
            let result = await response.json()
            setParams((prev) => ({
                ...prev,
                latitude: result.documents[0]?.y,
                longitude: result.documents[0]?.x,
            }))
        }

        getLatLon(fullAddress)
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const trimmedTitle = params.title.trim()

        if (!trimmedTitle.length) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('시설명을 입력해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('sportsId',id)
        formData.append('mainImage',params.mainImage)
        for (let i = 0; i < newUploadFiles.length; i++) {
            formData.append('images', newUploadFiles[i])
        }
        
        formData.append('newOldImages',uploadFiles)
        formData.append('type',params.type)
        formData.append('category',params.category)
        formData.append('title',trimmedTitle)
        formData.append('phone',params.phone)
        formData.append('address',params.address)
        formData.append('addressDetail',params.addressDetail)
        formData.append('latitude',params.latitude)
        formData.append('longitude',params.longitude)
        formData.append('openingHours',params.openingHours)
        formData.append('info',params.info)
        formData.append('neeGym',params.neeGym)
        formData.append('daGym',params.daGym)
        formData.append('phoneTime',params.phoneTime)
        formData.append('postTime',params.postTime)
        formData.append('memo',params.memo)

        let nextPrice = ''

        if (params.price1) {
            nextPrice = '1:' + params.price1
            if (params.price1Sale) nextPrice += ':' + params.price1Sale
        }

        if (params.price3) {
            if (nextPrice.length > 0) nextPrice += ','
            nextPrice += '3:' + params.price3
            if (params.price3Sale) nextPrice += ':' + params.price3Sale
        }

        if (params.price6) {
            if (nextPrice.length > 0) nextPrice += ','
            nextPrice += '6:' + params.price6
            if (params.price6Sale) nextPrice += ':' + params.price6Sale
        }

        if (params.price12) {
            if (nextPrice.length > 0) nextPrice += ','
            nextPrice += '12:' + params.price12
            if (params.price12Sale) nextPrice += ':' + params.price12Sale
        }

        formData.append('price', nextPrice)
        formData.append('options', [...checked].sort())

        updateData(formData)
    }

    const handleClick = (e) => {
        e.target.select()
    }

    const checkList = [
        { id: "1", name: "주차"}, 
        { id: "2", name: "샤워실"}, 
        { id: "3", name: "수건"}, 
        { id: "4", name: "운동복"}, 
        { id: "5", name: "인바디"}, 
        { id: "6", name: "1:1레슨"}, 
        { id: "7", name: "락커"}, 
        { id: "8", name: "탈의실"},
        { id: "9", name: "와이파이"}
    ]

    const handleChange = (e) => {
        var updatedList = [...checked]
        if (e.target.checked) {
            updatedList = [...checked, e.target.value]
        } else {
            updatedList.splice(checked.indexOf(e.target.value), 1)
        }
        setChecked(updatedList)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="운동시설 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={handleBackClick} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            <AddressSearchModal open={openModal} onClose={() => setOpenModal(false)} onComplete={handleComplete} />
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Prompt when={leaveGuardEnabled} message={SPORT_IMAGE_LEAVE_MESSAGE} />
                    {!hasDetail ? (
                        <>
                            <Title>운동시설 수정</Title>
                            {renderDetailErrorState()}
                            {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                        </>
                    ) : (
                    <>
                    <Title>운동시설 수정</Title>
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                        <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                                    <FormControl sx={{ maxWidth: 320 }}>
                                        <Card >
                                            <CardMedia component="img" image={mainImage} alt="메인이미지"/>
                                        </Card>
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                                    <FormControl>
                                        <TextField
                                            style={{ display: "none" }}
                                            name="mainImage"
                                            id="mainImage"
                                            type="file"
                                            inputProps={{ accept: "image/png,image/jpg,image/jpeg" }}
                                            onChange={e => handleUploadClick(e)}
                                        />
                                        <label htmlFor="mainImage">
                                            <Button variant="contained" color="success" component="span" startIcon={<Add />} sx={FORM_WIDE_UPLOAD_BUTTON_SX}>
                                            메인 이미지 업로드
                                            </Button>
                                        </label>
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <ImageList sx={{ maxHeight: 400, border: 1, borderColor: 'grey.500' }} cols={4} gap={8} >
                                    {uploadFiles.map((file, index) => (
                                        <ImageListItem key={index}>
                                            <img
                                                src={`${process.env.REACT_APP_AWS_S3_URL}/${file}`}
                                                srcSet={`${process.env.REACT_APP_AWS_S3_URL}/${file}`}
                                                alt={`${process.env.REACT_APP_AWS_S3_URL}/${file}`}
                                                loading="lazy"
                                            />
                                            <ImageListItemBar
                                                sx={{
                                                    background:
                                                    'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                                                    'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                                                }}
                                                title={''}
                                                position="top"
                                                actionIcon={
                                                    <IconButton sx={{ color: 'white' }} aria-label={`삭제`} onClick={() => handleImageDelete(index)}>
                                                        <DeleteIcon/>
                                                    </IconButton>
                                                }
                                                actionPosition="right"
                                            />
                                        </ImageListItem>
                                    ))}
                                    {newUploadFiles2.map((file, index) => (
                                        <ImageListItem key={uploadFiles.length + index}>
                                            <img
                                                src={file}
                                                srcSet={file}
                                                alt={file}
                                                loading="lazy"
                                            />
                                            <ImageListItemBar
                                                sx={{
                                                    background:
                                                    'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                                                    'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                                                }}
                                                title={''}
                                                position="top"
                                                actionIcon={
                                                    <IconButton sx={{ color: 'white' }} aria-label={`삭제`} onClick={() => handleNewImageDelete(index)}>
                                                        <DeleteIcon/>
                                                    </IconButton>
                                                }
                                                actionPosition="right"
                                            />
                                        </ImageListItem>
                                    ))}
                                </ImageList>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box 
                                    display="flex" 
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="center" 
                                    // sx={{ '& button': { m: 2 } }}
                                >
                                    <FormControl >
                                        <TextField
                                            style={{ display: "none"}}
                                            name="images"
                                            id="images"
                                            type="file"
                                            inputProps={{ multiple: true, accept: "image/png,image/jpg,image/jpeg" }}
                                            onChange={e => handleUploadClickMulti(e)}
                                            disabled={fileLimit}
                                        />
                                        <label htmlFor="images">
                                            <Button variant="contained" color="success" component="span" startIcon={<Add />} sx={FORM_WIDE_UPLOAD_BUTTON_SX} disabled={fileLimit}>
                                            이미지 업로드
                                            </Button>
                                        </label>
                                    </FormControl>
                                </Box>
                            </Grid>
                            
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth>
                                    <TextField
                                        label="시설타입"
                                        select
                                        id="type"
                                        name="type"
                                        value={params.type || 1}
                                        onChange={handleUpdateField} 
                                        size="small"
                                    >
                                        <MenuItem value={1}>무료</MenuItem>
                                        <MenuItem value={2}>요금제(하)</MenuItem>
                                        <MenuItem value={3}>요금제(중)</MenuItem>
                                        <MenuItem value={4}>요금제(상)</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                            {/* <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth>
                                    <TextField
                                        label="시설종류"
                                        select
                                        id="category"
                                        name="category"
                                        value={params.category || 1}
                                        onChange={handleUpdateField} 
                                        size="small"
                                    >
                                        <MenuItem value={1}>헬스</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid> */}
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="시설명"
                                        required
                                        id="title"
                                        name="title"
                                        value={params.title || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="주소"
                                        required
                                        id="address"
                                        name="address"
                                        value={params.address || ''}
                                        size="small"
                                        onClick={() => setOpenModal(true)}
                                        InputProps={{ readOnly: true, endAdornment: <SearchIcon color="action"/>}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="상세주소"
                                        id="addressDetail"
                                        name="addressDetail"
                                        value={params.addressDetail || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="연락처"
                                        required
                                        id="phone"
                                        name="phone"
                                        value={params.phone || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <FormLabel component="legend">시설옵션</FormLabel>
                                    <FormGroup aria-label="position" row>
                                        {checkList.map((item, index) => (
                                            <FormControlLabel
                                                key={index}
                                                control={<Checkbox onChange={handleChange} value={item.id}/>}
                                                checked={checked.includes(item.id)}
                                                label={item.name}
                                            />
                                        ))}
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="운영시간"
                                        required
                                        multiline
                                        rows={6}
                                        id="openingHours"
                                        name="openingHours"
                                        value={params.openingHours || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="매장정보"
                                        multiline
                                        rows={6}
                                        id="info"
                                        name="info"
                                        value={params.info || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="1개월 금액"
                                        id="price1"
                                        name="price1"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price1 || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="1개월 할인금액"
                                        id="price1Sale"
                                        name="price1Sale"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price1Sale || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="3개월 금액"
                                        id="price3"
                                        name="price3"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price3 || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="3개월 할인금액"
                                        id="price3Sale"
                                        name="price3Sale"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price3Sale || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="6개월 금액"
                                        id="price6"
                                        name="price6"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price6 || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="6개월 할인금액"
                                        id="price6Sale"
                                        name="price6Sale"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price6Sale || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="12개월 금액"
                                        id="price12"
                                        name="price12"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price12 || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6} >
                                <FormControl fullWidth sx={{ marginRight:1 }} >
                                    <TextField
                                        label="12개월 할인금액"
                                        id="price12Sale"
                                        name="price12Sale"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 90000000 } }}
                                        value={params.price12Sale || ''}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">니짐내짐</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-label="니짐내짐"
                                        name="neeGym"
                                        value={params.neeGym}
                                        onChange={handleUpdateField}
                                    >
                                        <FormControlLabel value={0} control={<Radio />} label="아니오" />
                                        <FormControlLabel value={1} control={<Radio />} label="예" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">다짐</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-label="다짐"
                                        name="daGym"
                                        value={params.daGym}
                                        onChange={handleUpdateField}
                                    >
                                        <FormControlLabel value={0} control={<Radio />} label="아니오" />
                                        <FormControlLabel value={1} control={<Radio />} label="예" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="전화영업일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={params.phoneTime}
                                            // minDate={params.endTime}
                                            onChange={d => setParams({ ...params, phoneTime: d })}
                                            renderInput={(params) => <TextField {...params} size="small" />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="우편영업일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={params.postTime}
                                            // minDate={params.endTime}
                                            onChange={d => setParams({ ...params, postTime: d })}
                                            renderInput={(params) => <TextField {...params} size="small" />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="메모"
                                        multiline
                                        rows={6}
                                        id="memo"
                                        name="memo"
                                        value={params.memo || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        </fieldset>
                        <BottomButtonGroup
                            variant="formAction"
                            visible={true}
                            submitDisabled={saving}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            backDisabled={saving}
                            onBackClick={handleBackClick}
                        />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                    </>
                    )}
                </Paper>
            )}
        </Grid>
    )
}
